const TABLE_ELEMENT_ID = "itsm-table";
const IMAGE_BLOCK_ELEMENT_ID = "itsm-table-image-block";
const EXCLUSION_CLASSES = ["itsm-clear"];

const filterExcludedElements = (node: HTMLElement) => {
  return !EXCLUSION_CLASSES.some((classname) =>
    node.classList?.contains(classname),
  );
};

export type GenerateItsmImage = (isDev?: boolean) => Promise<string>;

/**
 * Generates a PNG image from the ITSM table DOM element.
 * Captures the element with id="itsm-table", applies compression, and returns base64 data URL.
 *
 * Requires an element with id="itsm-table-image-block" to exist in the DOM
 * as a container for the cloned table during image generation.
 *
 * @returns Promise<string> - Base64 data URL of the generated image, or empty string on failure
 */
export const generateItsmImage: GenerateItsmImage = async (isDev = false) => {
  const { toPng } = await import("html-to-image");
  const table = document.getElementById(TABLE_ELEMENT_ID);
  const imageTable = document.getElementById(IMAGE_BLOCK_ELEMENT_ID);

  if (!table || !imageTable) {
    console.error("Table or image block container not found");
    return "";
  }

  // If the table is not visible (e.g. hidden on mobile), skip image generation
  if (table.offsetWidth === 0 || table.offsetHeight === 0) {
    return "";
  }

  const imageCompression = (await import("browser-image-compression")).default;

  const clone = table.cloneNode(true) as HTMLElement;

  // Remove the original id to avoid duplicate IDs in the DOM
  clone.removeAttribute("id");

  imageTable.appendChild(clone);

  const headers = imageTable.querySelectorAll(
    "[data-table='col-header']",
  ) as NodeListOf<HTMLElement>;
  headers.forEach((header) => {
    const svgs = header.querySelectorAll("svg") as NodeListOf<SVGSVGElement>;
    svgs.forEach((svg) => {
      svg.style.marginLeft = "8px";
    });
  });

  clone.style.width = "1292px";
  clone.setAttribute(
    "data-table-clone",
    `itsm-table-${(Math.random() + 1).toString(36).substring(7)}`,
  );

  try {
    const dataUrl = await toPng(clone, {
      cacheBust: true,
      filter: filterExcludedElements,
      skipFonts: true,
      preferredFontFormat: "woff2",
    });

    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], "ITSMtable.png", { type: blob.type });

    const options = {
      maxSizeMB: isDev ? 0.13 : 0.45,
      maxWidthOrHeight: isDev ? 1000 : 4000,
      alwaysKeepResolution: true,
    };

    const compressedBlob = await imageCompression(file, options);
    const compressedDataUrl =
      await imageCompression.getDataUrlFromFile(compressedBlob);

    return compressedDataUrl;
  } catch (err) {
    console.error("Error during image generation or compression:", err);
    return "";
  } finally {
    imageTable.removeChild(clone);
  }
};
